import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Layers } from 'lucide-react';

/**
 * SpaceSelectorDialog - Dialog for selecting which spaces to assign a qualification to
 * Used in stack view when assigning requirements to team members
 */
export const SpaceSelectorDialog = ({
  open,
  onOpenChange,
  spaces,
  memberName,
  qualificationName,
  onConfirm
}) => {
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);

  const handleToggleSpace = (spaceId) => {
    setSelectedSpaceIds(prev => {
      if (prev.includes(spaceId)) {
        return prev.filter(id => id !== spaceId);
      } else {
        return [...prev, spaceId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSpaceIds.length === spaces.length) {
      setSelectedSpaceIds([]);
    } else {
      setSelectedSpaceIds(spaces.map(s => s.id));
    }
  };

  const handleConfirm = () => {
    if (selectedSpaceIds.length === 0) return;
    onConfirm(selectedSpaceIds);
    setSelectedSpaceIds([]);
  };

  const handleCancel = () => {
    setSelectedSpaceIds([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-600" />
            Select Spaces
          </DialogTitle>
          <DialogDescription>
            Choose which space(s) to assign <span className="font-medium">{qualificationName}</span> to <span className="font-medium">{memberName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              {selectedSpaceIds.length} of {spaces.length} selected
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="h-8"
            >
              {selectedSpaceIds.length === spaces.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {spaces.map((space) => (
                <div
                  key={space.id}
                  className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleToggleSpace(space.id)}
                >
                  <Checkbox
                    checked={selectedSpaceIds.includes(space.id)}
                    onCheckedChange={() => handleToggleSpace(space.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {space.name}
                    </p>
                  </div>
                  {selectedSpaceIds.includes(space.id) && (
                    <Badge variant="default" className="ml-2">
                      Selected
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedSpaceIds.length === 0}
          >
            Assign to {selectedSpaceIds.length} Space{selectedSpaceIds.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
